import React, { useState, useEffect } from 'react';
import {
  UserIcon,
  TrophyIcon,
  FireIcon,
  SparklesIcon,
  GiftIcon,
  AcademicCapIcon,
  ChartBarIcon,
  CalendarDaysIcon,
  ClockIcon,
  ArrowTrendingUpIcon,
  BookOpenIcon,
  CheckCircleIcon,
  XMarkIcon,
  PowerIcon,
  ChevronDownIcon
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { gamificationActions } from '../utils/gamificationSystem';
import { getRecommendedStudyPath, getDetailedRecommendationReason } from '../utils/smartLearningNavigation';
import CommonHeader from './common/CommonHeader';

// 사용자 정의 애니메이션 스타일
const fadeInStyle = {
  '@keyframes fadeIn': {
    '0%': { opacity: 0, transform: 'translateY(-10px)' },
    '100%': { opacity: 1, transform: 'translateY(0)' }
  },
  '.animate-fadeIn': {
    animation: 'fadeIn 0.3s ease-in-out'
  }
};

// 스타일을 head에 주입
if (typeof document !== 'undefined') {
  const styleElement = document.getElementById('accordion-animations') || document.createElement('style');
  styleElement.id = 'accordion-animations';
  styleElement.innerHTML = `
    @keyframes fadeIn {
      0% { opacity: 0; transform: translateY(-10px); }
      100% { opacity: 1; transform: translateY(0); }
    }
    .animate-fadeIn { animation: fadeIn 0.3s ease-in-out; }
  `;
  if (!document.getElementById('accordion-animations')) {
    document.head.appendChild(styleElement);
  }
}

const PersonalDashboard = () => {
  const [stats, setStats] = useState(null);
  const [badges, setBadges] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [showAllBadges, setShowAllBadges] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [expandedSections, setExpandedSections] = useState(new Set(['quickActions'])); // 기본적으로 빠른 액션만 펼침
  const [smartRecommendation, setSmartRecommendation] = useState(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    updateStats();
    updateBadges();
    generateRecentActivity();
    loadSmartRecommendation();

    // 화면 크기 감지
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);

    // 게이미피케이션 이벤트 리스너
    const handleStatsUpdate = () => {
      updateStats();
      updateBadges();
      generateRecentActivity();
    };

    window.addEventListener('gamificationpointsEarned', handleStatsUpdate);
    window.addEventListener('gamificationbadgesEarned', handleStatsUpdate);

    return () => {
      window.removeEventListener('resize', checkIsMobile);
      window.removeEventListener('gamificationpointsEarned', handleStatsUpdate);
      window.removeEventListener('gamificationbadgesEarned', handleStatsUpdate);
    };
  }, [user?.id]);

  const updateStats = () => {
    const userId = user?.id || 'guest';
    const currentStats = gamificationActions.getStats(userId);
    setStats(currentStats);
  };

  const updateBadges = () => {
    const userId = user?.id || 'guest';
    const earnedBadges = gamificationActions.getEarnedBadges(userId);
    setBadges(earnedBadges);
  };

  const loadSmartRecommendation = () => {
    try {
      const userId = user?.id || 'guest';
      const recommendation = getRecommendedStudyPath(userId);
      const detailedReasons = getDetailedRecommendationReason(userId);
      
      setSmartRecommendation({
        ...recommendation,
        detailedReasons
      });
    } catch (error) {
      console.error('Error loading smart recommendation:', error);
      // 기본 추천 설정
      setSmartRecommendation({
        recommendationType: 'default',
        examType: 'information-processing-engineer',
        year: 2023,
        session: 1,
        startQuestionNumber: 1,
        reason: '새로운 학습을 시작해보세요',
        detailedReasons: ['🌟 새로운 학습 여정을 시작해보세요']
      });
    }
  };

  const handleStartSmartLearning = () => {
    if (!smartRecommendation) return;
    
    console.log('Smart learning click:', {
      recommendationType: smartRecommendation.recommendationType,
      examType: smartRecommendation.examType
    });
    
    // 진단평가 완료 후라면 회차 선택 페이지로 이동
    if (smartRecommendation.recommendationType === 'afterDiagnostic') {
      console.log('Navigating to exam selection for:', smartRecommendation.examType);
      navigate(`/${smartRecommendation.examType}`);
    } else {
      // 일반적인 스마트 학습 시작 (직접 CBT 페이지로)
      console.log('Starting smart learning directly for:', smartRecommendation.examType);
      navigate(`/${smartRecommendation.examType}/${smartRecommendation.year}/${smartRecommendation.session}`, {
        state: { startQuestionNumber: smartRecommendation.startQuestionNumber }
      });
    }
  };

  const generateRecentActivity = () => {
    // 모의 최근 활동 데이터
    const activities = [
      { id: 1, type: 'badge', icon: '🏆', text: '학습 시작 배지를 획득했습니다', time: '2시간 전' },
      { id: 2, type: 'study', icon: '📚', text: '전기기사 10문제를 풀었습니다', time: '3시간 전' },
      { id: 3, type: 'streak', icon: '🔥', text: '5연속 정답을 달성했습니다', time: '1일 전' },
      { id: 4, type: 'level', icon: '⭐', text: '레벨 2로 승급했습니다', time: '2일 전' },
      { id: 5, type: 'exam', icon: '✅', text: '정보처리기사 모의고사를 완료했습니다', time: '3일 전' }
    ];
    setRecentActivity(activities);
  };

  const toggleSection = (sectionId) => {
    if (!isMobile) return; // 태블릿 이상에서는 토글 비활성화
    
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  const isSectionExpanded = (sectionId) => {
    return !isMobile || expandedSections.has(sectionId);
  };

  const handleLogout = async () => {
    if (window.confirm('정말 로그아웃하시겠습니까?')) {
      try {
        await logout();
        // 로그아웃 후 메인 페이지로 이동
        navigate('/');
      } catch (error) {
        console.error('Logout failed:', error);
        alert('로그아웃 중 오류가 발생했습니다.');
      }
    }
  };

  const formatTime = (seconds) => {
    if (seconds < 60) return `${seconds}초`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}분`;
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return minutes > 0 ? `${hours}시간 ${minutes}분` : `${hours}시간`;
  };

  const getBadgesByRarity = () => {
    const rarityOrder = ['legendary', 'epic', 'rare', 'uncommon', 'common'];
    const grouped = badges.reduce((acc, badge) => {
      if (!acc[badge.rarity]) acc[badge.rarity] = [];
      acc[badge.rarity].push(badge);
      return acc;
    }, {});

    return rarityOrder.reduce((acc, rarity) => {
      if (grouped[rarity]) {
        acc[rarity] = grouped[rarity];
      }
      return acc;
    }, {});
  };

  const getRarityColor = (rarity) => {
    const colors = {
      common: 'text-gray-600 bg-gray-100 border-gray-300',
      uncommon: 'text-green-600 bg-green-100 border-green-300',
      rare: 'text-blue-600 bg-blue-100 border-blue-300',
      epic: 'text-purple-600 bg-purple-100 border-purple-300',
      legendary: 'text-yellow-600 bg-yellow-100 border-yellow-300'
    };
    return colors[rarity] || colors.common;
  };

  const getRarityLabel = (rarity) => {
    const labels = {
      common: '일반',
      uncommon: '고급',
      rare: '희귀',
      epic: '영웅',
      legendary: '전설'
    };
    return labels[rarity] || rarity;
  };

  // 아코디언 헤더 컴포넌트
  const AccordionHeader = ({ sectionId, icon: Icon, title, summary, iconColor = 'text-orange-500' }) => {
    const isExpanded = isSectionExpanded(sectionId);
    
    return (
      <div 
        className={`
          flex items-center justify-between transition-all duration-200 border-b border-gray-100
          ${isMobile 
            ? 'p-4 cursor-pointer hover:bg-gray-50 active:bg-gray-100 touch-manipulation' 
            : 'p-4 cursor-default'
          }
        `}
        onClick={() => toggleSection(sectionId)}
        style={{ minHeight: '64px' }} // 터치 친화적 최소 높이
      >
        <div className="flex items-center space-x-3">
          <Icon className={`w-5 h-5 ${iconColor} transition-colors duration-200`} />
          <h3 className="text-lg font-semibold text-gray-900 select-none">{title}</h3>
          {summary && (
            <span className="text-sm text-gray-500 ml-2 select-none">• {summary}</span>
          )}
        </div>
        
        {isMobile && (
          <ChevronDownIcon 
            className={`w-5 h-5 text-gray-400 transition-all duration-300 ease-in-out ${
              isExpanded ? 'rotate-180 text-gray-600' : 'rotate-0'
            }`} 
          />
        )}
      </div>
    );
  };

  if (!stats) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-orange-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-orange-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-600">대시보드를 로딩 중...</p>
        </div>
      </div>
    );
  }

  const badgesByRarity = getBadgesByRarity();

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50">
      <CommonHeader />

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* 페이지 타이틀 */}
        <div className="mb-8">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
            {user?.name || user?.email?.split('@')[0] || '사용자'}님의 대시보드
          </h1>
          <p className="text-gray-600 mt-2">학습 진행 상황과 성과를 확인하세요</p>
        </div>
        {/* Quick Actions - 최상단 배치 */}
        <div className="bg-white rounded-2xl shadow-xl mb-8 overflow-hidden">
          <AccordionHeader 
            sectionId="quickActions" 
            icon={GiftIcon} 
            title="빠른 액션" 
            summary="3개 메뉴"
            iconColor="text-orange-500"
          />
          
          {isSectionExpanded('quickActions') && (
            <div className="px-8 pb-8 transition-all duration-300 ease-in-out animate-fadeIn">
              <div className="grid md:grid-cols-3 gap-4">
            <button 
              onClick={handleStartSmartLearning}
              className="flex items-center p-4 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg hover:from-orange-600 hover:to-orange-700 transition-all relative"
              title={smartRecommendation?.detailedReasons?.join('\n')}
            >
              <BookOpenIcon className="w-6 h-6 mr-3 flex-shrink-0" />
              <div className="text-left flex-1">
                <h4 className="font-bold text-base">
                  {smartRecommendation?.recommendationType === 'continue' ? '학습 이어하기' :
                   smartRecommendation?.recommendationType === 'wrongAnswerReview' ? '오답 복습하기' :
                   smartRecommendation?.recommendationType === 'nextSession' ? '다음 회차 도전' :
                   smartRecommendation?.recommendationType === 'afterDiagnostic' ? '회차 선택하기' :
                   '학습 시작하기'}
                </h4>
                <p className="text-orange-100 text-xs">
                  {smartRecommendation?.recommendationType === 'afterDiagnostic' 
                    ? '원하는 회차를 선택해주세요' 
                    : (smartRecommendation?.reason || '새로운 문제에 도전')}
                </p>
              </div>
              
              {/* 자격증 배지 */}
              {smartRecommendation?.targetCertification && (
                <div className="absolute -top-2 -right-2 bg-blue-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg">
                  {smartRecommendation.targetCertification}
                </div>
              )}
              
              {/* 문제 번호 배지 */}
              {smartRecommendation?.startQuestionNumber > 1 && !smartRecommendation?.targetCertification && (
                <div className="absolute -top-1 -right-1 bg-yellow-400 text-yellow-900 text-xs font-bold px-2 py-1 rounded-full">
                  #{smartRecommendation.startQuestionNumber}
                </div>
              )}
            </button>
            
            <button 
              onClick={() => navigate('/wrong-answers')}
              className="flex items-center p-4 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition-all"
            >
              <CheckCircleIcon className="w-6 h-6 mr-3 flex-shrink-0" />
              <div className="text-left">
                <h4 className="font-bold text-base">오답노트</h4>
                <p className="text-red-100 text-xs">틀린 문제 다시보기</p>
              </div>
            </button>
            
            <button 
              onClick={() => navigate('/progress')}
              className="flex items-center p-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all"
            >
              <ChartBarIcon className="w-6 h-6 mr-3 flex-shrink-0" />
              <div className="text-left">
                <h4 className="font-bold text-base">학습 진도</h4>
                <p className="text-blue-100 text-xs">상세한 학습 현황</p>
              </div>
            </button>
              </div>
            </div>
          )}
        </div>

        {/* Level & Progress Overview */}
        <div className="bg-white rounded-2xl shadow-xl mb-8 overflow-hidden">
          <AccordionHeader 
            sectionId="levelProgress" 
            icon={TrophyIcon} 
            title="레벨 & 진행 상황" 
            summary={`Lv.${stats.level} ${stats.levelTitle}`}
            iconColor="text-blue-500"
          />
          
          {isSectionExpanded('levelProgress') && (
            <div className="px-8 pb-8 transition-all duration-300 ease-in-out animate-fadeIn">
              <div className="flex items-center justify-between mb-6">
            <div className="flex items-center space-x-4">
              <div 
                className="w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-bold"
                style={{ backgroundColor: stats.levelColor }}
              >
                {stats.levelIcon}
              </div>
              <div>
                <div className="flex items-center space-x-3">
                  <span className="text-3xl font-bold text-gray-900">Lv.{stats.level}</span>
                  <span className="text-xl text-gray-600">{stats.levelTitle}</span>
                </div>
                <div className="text-lg text-gray-500">{stats.points.toLocaleString()} 포인트</div>
              </div>
            </div>
            
            <div className="text-right">
              <div className="text-sm text-gray-500">다음 레벨까지</div>
              <div className="text-2xl font-bold text-orange-600">
                {stats.pointsToNextLevel > 0 ? `${stats.pointsToNextLevel.toLocaleString()}pt` : '최고 레벨!'}
              </div>
              <div className="text-sm text-gray-500">
                {stats.pointsToNextLevel > 0 && `진행률: ${stats.progress}%`}
              </div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="mb-6">
            <div className="w-full bg-gray-200 rounded-full h-4">
              <div 
                className="h-4 rounded-full transition-all duration-1000"
                style={{ 
                  width: `${stats.progress}%`,
                  backgroundColor: stats.levelColor 
                }}
              ></div>
            </div>
          </div>

          {/* Quick Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-blue-50 rounded-xl border border-blue-200">
              <AcademicCapIcon className="w-8 h-8 text-blue-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-blue-900">{stats.totalCorrectAnswers.toLocaleString()}</div>
              <div className="text-sm text-blue-700 font-medium">정답 문제</div>
            </div>
            
            <div className="text-center p-4 bg-green-50 rounded-xl border border-green-200">
              <TrophyIcon className="w-8 h-8 text-green-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-green-900">{stats.totalExamsCompleted.toLocaleString()}</div>
              <div className="text-sm text-green-700 font-medium">완료 시험</div>
            </div>
            
            <div className="text-center p-4 bg-red-50 rounded-xl border border-red-200">
              <FireIcon className="w-8 h-8 text-red-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-red-900">{stats.maxStreak}</div>
              <div className="text-sm text-red-700 font-medium">최고 연승</div>
            </div>
            
            <div className="text-center p-4 bg-purple-50 rounded-xl border border-purple-200">
              <SparklesIcon className="w-8 h-8 text-purple-600 mx-auto mb-2" />
              <div className="text-2xl font-bold text-purple-900">{stats.badgeCount}</div>
              <div className="text-sm text-purple-700 font-medium">획득 배지</div>
            </div>
          </div>
            </div>
          )}
        </div>

        <div className="grid lg:grid-cols-2 gap-8 mb-8">
          {/* Detailed Statistics */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <AccordionHeader 
              sectionId="detailedStats" 
              icon={ChartBarIcon} 
              title="상세 학습 통계" 
              summary={`${stats.totalCorrectAnswers}문제 정답`}
              iconColor="text-orange-500"
            />
            
            {isSectionExpanded('detailedStats') && (
              <div className="px-8 pb-8 transition-all duration-300 ease-in-out animate-fadeIn">
            
            <div className="space-y-4">
              <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <FireIcon className="w-5 h-5 text-orange-500" />
                  <span className="font-medium text-gray-700">현재 연승</span>
                </div>
                <span className="text-xl font-bold text-orange-600">{stats.currentStreak}</span>
              </div>
              
              <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <ClockIcon className="w-5 h-5 text-blue-500" />
                  <span className="font-medium text-gray-700">총 학습 시간</span>
                </div>
                <span className="text-xl font-bold text-blue-600">{formatTime(stats.totalStudyTime)}</span>
              </div>
              
              <div className="flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <ArrowTrendingUpIcon className="w-5 h-5 text-green-500" />
                  <span className="font-medium text-gray-700">평균 세션 시간</span>
                </div>
                <span className="text-xl font-bold text-green-600">{formatTime(stats.averageSessionTime)}</span>
              </div>

              {/* Progress Chart Placeholder */}
              <div className="mt-6 p-4 bg-gradient-to-r from-orange-100 to-orange-200 rounded-lg">
                <div className="text-center">
                  <BookOpenIcon className="w-12 h-12 text-orange-600 mx-auto mb-2" />
                  <p className="text-orange-800 font-medium">학습 차트</p>
                  <p className="text-sm text-orange-700">상세한 진행 그래프 (준비 중)</p>
                </div>
              </div>
            </div>
              </div>
            )}
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <AccordionHeader 
              sectionId="recentActivity" 
              icon={CalendarDaysIcon} 
              title="최근 활동" 
              summary={`${recentActivity.length}개 활동`}
              iconColor="text-green-500"
            />
            
            {isSectionExpanded('recentActivity') && (
              <div className="px-8 pb-8 transition-all duration-300 ease-in-out animate-fadeIn">
            
            <div className="space-y-4">
              {recentActivity.map((activity) => (
                <div key={activity.id} className="flex items-start space-x-4 p-3 hover:bg-gray-50 rounded-lg transition-colors">
                  <div className="text-2xl">{activity.icon}</div>
                  <div className="flex-1">
                    <p className="text-gray-800 font-medium">{activity.text}</p>
                    <p className="text-sm text-gray-500">{activity.time}</p>
                  </div>
                </div>
              ))}
              
              {recentActivity.length === 0 && (
                <div className="text-center text-gray-500 py-8">
                  <CalendarDaysIcon className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                  <p>아직 학습 기록이 없습니다</p>
                  <p className="text-sm">문제를 풀어서 활동을 시작해보세요!</p>
                </div>
              )}
            </div>
              </div>
            )}
          </div>
        </div>

        {/* Achievements & Badges */}
        <div className="bg-white rounded-2xl shadow-xl mb-8 overflow-hidden">
          <AccordionHeader 
            sectionId="badges" 
            icon={SparklesIcon} 
            title="보유 배지" 
            summary={`${badges.length}개 획득`}
            iconColor="text-purple-500"
          />
          
          {isSectionExpanded('badges') && (
            <div className="px-8 pb-8 transition-all duration-300 ease-in-out animate-fadeIn">
              <div className="flex items-center justify-between mb-6">
                {badges.length > 6 && (
                  <button
                    onClick={() => setShowAllBadges(!showAllBadges)}
                    className="text-sm text-purple-600 hover:text-purple-700 font-medium bg-purple-100 px-4 py-2 rounded-lg hover:bg-purple-200 transition-colors ml-auto"
                  >
                    {showAllBadges ? '접기' : '전체 보기'}
                  </button>
                )}
              </div>

          {badges.length > 0 ? (
            <div className="space-y-6">
              {Object.entries(badgesByRarity).map(([rarity, rarityBadges]) => (
                <div key={rarity} className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <span className={`text-sm font-bold px-3 py-1 rounded-full ${getRarityColor(rarity)}`}>
                      {getRarityLabel(rarity)} ({rarityBadges.length})
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {(showAllBadges ? rarityBadges : rarityBadges.slice(0, 3)).map((badge) => (
                      <div
                        key={badge.id}
                        className={`p-4 rounded-xl border-2 ${getRarityColor(badge.rarity)} hover:shadow-lg transition-all duration-200`}
                      >
                        <div className="flex items-center space-x-3 mb-2">
                          <span className="text-3xl">{badge.icon}</span>
                          <div>
                            <h4 className="font-bold text-gray-900">{badge.name}</h4>
                            <span className={`text-xs font-medium px-2 py-1 rounded-full ${getRarityColor(badge.rarity)}`}>
                              {getRarityLabel(badge.rarity)}
                            </span>
                          </div>
                        </div>
                        <p className="text-sm text-gray-600 leading-relaxed">{badge.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center text-gray-500 py-12">
              <SparklesIcon className="w-16 h-16 mx-auto mb-4 text-gray-400" />
              <p className="text-lg font-medium mb-2">아직 획득한 배지가 없습니다</p>
              <p className="text-sm">문제를 풀고 목표를 달성해서 배지를 모아보세요!</p>
            </div>
          )}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default PersonalDashboard;